import { m } from "@plannello/i18n/messages";
import type { QueryClause } from "@plannello/shared";
import type { ReactNode } from "react";
import { PRIORITY_VALUES, PriorityIcon, priorityLabel, priorityToken } from "@/components/domain/PriorityIcon";
import { StatusIcon } from "@/components/domain/StatusIcon";
import { TypeIcon } from "@/components/domain/TypeIcon";
import { Avatar } from "@/components/ui";
import type { Catalog } from "@/data/catalog";
import { labelSwatch, loc } from "@/lib/format";

/**
 * Autocompletion and chip labels for the filter bar, driven by the schema. Values are the
 * query-language tokens (slugs, keys, p0..p3) so the text the user sees is exactly what runs.
 */

export type Suggestion = {
  /** Text inserted in place of the current token. */
  insert: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  /** A key suggestion leaves the caret after ":"; a value suggestion completes the clause. */
  kind: "key" | "value";
};

export type ValueOption = { token: string; label: string; icon?: ReactNode; description?: string };

const quote = (v: string) => (/[\s,"]/.test(v) ? `"${v.replaceAll('"', "")}"` : v);

/** Built-in keys offered by autocomplete, with a short description. */
function builtinKeys(): Array<{ key: string; description: string }> {
  return [
    { key: "status", description: m.qk_status() },
    { key: "priority", description: m.qk_priority() },
    { key: "type", description: m.qk_type() },
    { key: "assignee", description: m.qk_assignee() },
    { key: "label", description: m.qk_label() },
    { key: "is", description: m.qk_is() },
    { key: "has", description: m.qk_has() },
    { key: "product", description: m.qk_product() },
    { key: "milestone", description: m.qk_milestone() },
    { key: "parent", description: m.qk_parent() },
    { key: "sort", description: m.qk_sort() },
  ];
}

/** Human name of a query key ("status" -> "Status", "area" -> the Field's name). */
export function keyLabel(key: string, catalog: Catalog): string {
  switch (key) {
    case "status":
      return m.prop_status();
    case "priority":
      return m.prop_priority();
    case "type":
      return m.prop_type();
    case "assignee":
      return m.prop_assignees();
    case "label":
      return m.prop_labels();
    case "product":
      return m.prop_product();
    case "milestone":
      return m.prop_milestone();
    case "parent":
      return m.prop_parent();
    case "sort":
      return m.view_sort_by();
    case "is":
      return m.filter_key_is();
    case "has":
      return m.filter_key_has();
    default: {
      const g = catalog.labelGroups.find((x) => x.slug === key);
      return g ? loc(g.name) : key;
    }
  }
}

const IS_VALUES = ["open", "closed", "blocked", "mine", "unassigned", "archived"];
const HAS_VALUES = ["parent", "children", "assignee", "label", "milestone", "due", "start", "estimate"];
const SORT_VALUES = ["rank", "priority", "updated", "created", "due", "title", "number"];

/** Words for the fixed query values, so chips read "Is: open" / "Ist: offen" in either language. */
const IS_LABELS: Record<string, () => string> = {
  open: () => m.filter_is_open(),
  closed: () => m.filter_is_closed(),
  blocked: () => m.filter_is_blocked(),
  mine: () => m.filter_is_mine(),
  unassigned: () => m.filter_is_unassigned(),
  archived: () => m.filter_is_archived(),
};
const HAS_LABELS: Record<string, () => string> = {
  parent: () => m.filter_has_parent(),
  children: () => m.filter_has_children(),
  assignee: () => m.filter_has_assignee(),
  label: () => m.filter_has_label(),
  milestone: () => m.filter_has_milestone(),
  due: () => m.filter_has_due(),
  start: () => m.filter_has_start(),
  estimate: () => m.filter_has_estimate(),
};
const SORT_LABELS: Record<string, () => string> = {
  rank: () => m.sort_rank(),
  priority: () => m.sort_priority(),
  updated: () => m.sort_updated(),
  created: () => m.sort_created(),
  due: () => m.sort_due(),
  title: () => m.sort_title(),
  number: () => m.sort_number(),
};
/** Human label of a sort key ("updated" to "Last updated"); unknown keys stay as typed. */
export const sortKeyLabel = (key: string) => SORT_LABELS[key]?.() ?? key;

/** Every value a key accepts (for suggestions and the chip picker). */
export function valuesFor(key: string, catalog: Catalog, meName?: string): ValueOption[] {
  switch (key) {
    case "status": {
      const seen = new Set<string>();
      const out: ValueOption[] = [];
      for (const s of catalog.statusById.values()) {
        if (seen.has(s.slug)) continue;
        seen.add(s.slug);
        out.push({ token: s.slug, label: loc(s.name), icon: <StatusIcon category={s.category} decorative /> });
      }
      out.sort((a, b) => a.token.localeCompare(b.token));
      return [{ token: "open", label: "open" }, { token: "closed", label: "closed" }, ...out];
    }
    case "priority":
      return PRIORITY_VALUES.map((v) => ({
        token: priorityToken(v),
        label: priorityLabel(v),
        icon: <PriorityIcon value={v} />,
      }));
    case "type":
      return catalog.types.map((t) => ({
        token: t.slug,
        label: loc(t.name),
        icon: <TypeIcon name={t.icon} color={t.color} decorative />,
      }));
    case "assignee":
      return [
        { token: "me", label: meName ? m.picker_you({ name: meName }) : "me" },
        { token: "none", label: m.unassigned() },
        ...catalog.users.map((u) => ({
          token: quote(u.email ?? u.name),
          label: u.name,
          icon: <Avatar name={u.name} seed={u.id} src={u.image} bot={u.kind === "bot"} size="xs" labelled={false} />,
        })),
      ];
    case "label":
      return catalog.labels.map((l) => {
        const g = l.groupId ? catalog.groupById.get(l.groupId) : undefined;
        return {
          token: g ? `${g.slug}/${l.slug}` : l.slug,
          label: g ? `${loc(g.name)}: ${loc(l.name)}` : loc(l.name),
          icon: (
            <span
              aria-hidden="true"
              className="size-2 rounded-full"
              style={{ backgroundColor: labelSwatch(l.color) }}
            />
          ),
        };
      });
    case "product":
      return catalog.products.map((p) => ({
        token: p.key,
        label: p.isSpaceProduct ? `${p.name}` : p.name,
        description: p.key,
      }));
    case "milestone":
      return catalog.milestones.map((ms) => ({
        token: quote(ms.version ?? ms.name),
        label: ms.name,
        description: ms.version ?? undefined,
      }));
    case "is":
      return IS_VALUES.map((v) => ({ token: v, label: IS_LABELS[v]!() }));
    case "has":
      return HAS_VALUES.map((v) => ({ token: v, label: HAS_LABELS[v]!() }));
    case "sort":
      return SORT_VALUES.map((v) => ({ token: v, label: sortKeyLabel(v) }));
    default: {
      const g = catalog.labelGroups.find((x) => x.slug === key);
      if (!g) return [];
      return [
        { token: "none", label: m.no_field_value({ field: loc(g.name) }) },
        ...catalog.labels
          .filter((l) => l.groupId === g.id)
          .map((l) => ({
            token: l.slug,
            label: loc(l.name),
            icon: (
              <span
                aria-hidden="true"
                className="size-2 rounded-full"
                style={{ backgroundColor: labelSwatch(l.color) }}
              />
            ),
          })),
      ];
    }
  }
}

const lower = (s: string) => s.toLowerCase();

/** Suggestions for the token the caret is in (`sta` -> keys, `status:in` -> values). */
export function suggest(token: string, catalog: Catalog, meName?: string): Suggestion[] {
  const neg = token.startsWith("-") ? "-" : "";
  const t = neg ? token.slice(1) : token;
  const colon = t.indexOf(":");
  if (colon < 0) {
    if (t.length === 0) return [];
    const fields = catalog.labelGroups.map((g) => ({
      key: g.slug,
      description: `${m.qk_field()}: ${loc(g.name)}`,
    }));
    return [...builtinKeys(), ...fields]
      .filter((k) => lower(k.key).startsWith(lower(t)) && lower(k.key) !== lower(t))
      .slice(0, 8)
      .map((k) => ({
        insert: `${neg}${k.key}:`,
        label: `${k.key}:`,
        description: k.description,
        kind: "key" as const,
      }));
  }
  const key = lower(t.slice(0, colon));
  const rest = t.slice(colon + 1);
  const op = /^(>=|<=|>|<|=)/.exec(rest)?.[1] ?? "";
  const valuePart = rest.slice(op.length);
  const lastComma = valuePart.lastIndexOf(",");
  const done = lastComma >= 0 ? valuePart.slice(0, lastComma + 1) : "";
  const partial = lower(valuePart.slice(lastComma + 1).replace(/^"/, ""));
  return valuesFor(key, catalog, meName)
    .filter((v) => {
      if (partial === "") return true;
      return lower(v.token).replace(/^"/, "").startsWith(partial) || lower(v.label).includes(partial);
    })
    .filter((v) => lower(v.token) !== partial)
    .slice(0, 10)
    .map((v) => ({
      insert: `${neg}${key}:${op}${done}${v.token}`,
      label: v.label,
      description: v.token === v.label ? undefined : v.token,
      icon: v.icon,
      kind: "value" as const,
    }));
}

/** Human value labels of a recognised clause, for its chip. */
export function clauseParts(
  c: QueryClause,
  catalog: Catalog,
  meName?: string,
): { key: string; keyLabel: string; values: string[] } | null {
  const key = c.kind === "field" ? c.field : c.fieldRef?.key;
  if (!key) return null;
  const values = c.kind === "field" ? c.values : (c.fieldRef?.values ?? []);
  const options = valuesFor(key, catalog, meName);
  const label = (v: string) =>
    options.find((o) => lower(o.token.replace(/"/g, "")) === lower(v))?.label ??
    (key === "status"
      ? loc([...catalog.statusById.values()].find((s) => lower(loc(s.name)) === lower(v))?.name) || v
      : v);
  const op = c.kind === "field" && c.op !== "=" ? `${c.op} ` : "";
  return { key, keyLabel: keyLabel(key, catalog), values: values.map((v) => `${op}${label(v)}`) };
}

/**
 * The catalog as seen from a view: labels, statuses and milestones of the products in scope only,
 * labels de-duplicated by Field and slug (the query matches slugs, so one suggestion is enough).
 */
export function scopeCatalog(catalog: Catalog, productIds: readonly string[]): Catalog {
  if (productIds.length === 0) return catalog;
  const schemas = productIds.map((id) => catalog.schemaOf(id)).filter((s) => !!s);
  const labelIds = new Set(schemas.flatMap((s) => s.labels.map((l) => l.id)));
  const workflows = new Set(schemas.map((s) => s.product.workflowId));
  const products = new Set(productIds);
  const seen = new Set<string>();
  const labels = catalog.labels.filter((l) => {
    if (!labelIds.has(l.id)) return false;
    const k = `${l.groupId ?? ""}/${l.slug}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  const statusById = new Map([...catalog.statusById].filter(([, s]) => workflows.has(s.workflowId)));
  return {
    ...catalog,
    labels,
    statusById,
    milestones: catalog.milestones.filter((ms) => products.has(ms.productId)),
  };
}
